'use client';

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Drawer,
  Typography,
  Box,
  Button,
  CircularProgress,
  UnsavedChangesDialog,
} from 'src/components';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import useGetAccountFolders from 'src/hooks/accountFolders/useGetAccountFolders';
import {
  useGetFilterSetByIdQuery,
  useUpdateFilterSetMutation,
} from 'src/api/redux/filterSets';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { useNotification } from 'src/context/notification';
import { BillingCodes, SaveVolumeType } from '../CreateVolumeType/components';
import SearchPreview from '../EncountersDrawer/components/SearchPreview';
import SaveNewVolumeDialog from '../SaveNewVolumeDialog/SaveNewVolumeDialog';
import { SavedType, initialEncountersState, VolumeType, EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { mapContextToCodeType } from 'src/components/Encounters/utils';

const EditCodeSetsDrawer = (): React.ReactElement => {
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const { state, dispatch } = useEncounters();
  const { setNotification } = useNotification();
  const [originalState, setOriginalState] = useState<any>(null);
  const [isSaveAsNewDialogOpen, setIsSaveAsNewDialogOpen] = useState(false);

  // RTK Query mutation hook
  const [updateFilterSet, { isLoading }] = useUpdateFilterSetMutation();

  // Determine the ID to fetch - only when drawer is open and we have an editing ID
  const shouldFetch =
    isDrawerOpen('editCodeSetsDrawer') &&
    state.editingVolumeType === 'filterSet' &&
    !!state.editingVolumeId;

  // Use RTK Query directly to fetch the current code set by ID
  const {
    data: currentCodeSet,
    error: errorCurrentCodeSet,
    isLoading: isFetching,
  } = useGetFilterSetByIdQuery(
    { id: state.editingVolumeId || '' },
    { skip: !shouldFetch }
  );

  // Get account folders mutation function
  const { mutate: mutateAccountFolders } = useGetAccountFolders();

  // Get saved volume functions to update selected volume if needed
  const location = state.editingLocation || 'discovery';
  const { appliedSavedVolume, setSavedVolume } = useSavedVolume(location);

  // Create the reset state using useMemo to avoid recalculations
  const resetState = useMemo(() => {
    // Only process data if this is the correct type and we have the current code set
    if (
      !currentCodeSet ||
      state.editingVolumeType !== 'filterSet' ||
      currentCodeSet.id.toString() !== state.editingVolumeId
    )
      return null;

    // Create the reset state object
    const newResetState = {
      ...initialEncountersState,
      editingVolumeId: state.editingVolumeId,
      newCohortName: currentCodeSet.label,
      selectedType:
        currentCodeSet.scope?.toLowerCase() === 'personal'
          ? SavedType.PERSONAL
          : SavedType.COMPANY,
      editingVolumeType: state.editingVolumeType,
      // For filter sets, we keep the default volumeType (TOTAL_CLAIMS)
      billingCodes: {
        anyAllOperator: 'AND', // Filter sets always use AND
        groups: [],
      },
    };

    // Process filter data
    if (currentCodeSet.filters && currentCodeSet.filters.length > 0) {
      // Filter sets only have one group
      const groupId = `group-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      // Process all filters and combine their values
      const mappedCodes = currentCodeSet.filters.flatMap((filter) =>
        filter.values.map((value: string) => {
          // Use indexOf to find the first hyphen - codes can contain internal hyphens
          const hyphenIndex = value.indexOf('-');
          const codeType = hyphenIndex > -1 ? value.substring(0, hyphenIndex) : '';
          const codeName = hyphenIndex > -1 ? value.substring(hyphenIndex + 1) : value;
          return {
            id: value,
            code: codeName,
            context_type: codeType,
            description: '',
          };
        })
      );

      // Get the exclude status from the first filter (assuming all filters have the same status)
      const isExclude = currentCodeSet.filters[0]?.is_exclude || false;

      newResetState.billingCodes.groups = [
        {
          id: groupId,
          operator: 'ANY', // Filter sets use ANY by default
          inverse: isExclude,
          codes: mappedCodes,
        },
      ];
    }

    return newResetState;
  }, [currentCodeSet, state.editingVolumeId, state.editingVolumeType]);

  // Handle initialization when data is loaded and resetState is ready
  useEffect(() => {
    if (
      resetState &&
      state.editingVolumeId &&
      state.editingVolumeType === 'filterSet'
    ) {
      // Store the original state for comparison
      setOriginalState({
        newCohortName: resetState.newCohortName,
        selectedType: resetState.selectedType,
        billingCodes: JSON.parse(JSON.stringify(resetState.billingCodes)),
      });

      // Dispatch a reset action to completely replace the state
      dispatch({
        type: EncountersActionTypes.RESET_ENCOUNTERS_STATE,
        payload: resetState,
      });
    }
  }, [resetState, state.editingVolumeId, state.editingVolumeType, dispatch]);

  const handleClose = useCallback(() => {
    if (isSaveAsNewDialogOpen) return;

    closeDrawer('editCodeSetsDrawer');

    // Reset SaveAsNew dialog state
    setIsSaveAsNewDialogOpen(false);

    // Clear editing state
    dispatch({
      type: EncountersActionTypes.SET_EDITING_GROUP_ID,
      payload: { groupId: null },
    });
    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_ID,
      payload: { volumeId: null },
    });
    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_TYPE,
      payload: { volumeType: null },
    });
  }, [isSaveAsNewDialogOpen, closeDrawer, dispatch]);

  const handleSaveAsNew = () => {
    setIsSaveAsNewDialogOpen(true);
  };

  // Then update the buildUpdateRequestBody function:
  const buildUpdateRequestBody = () => {
    if (!originalState || !currentCodeSet) return {};

    const updateBody: any = {};

    // Check if label changed
    if (state.newCohortName !== originalState.newCohortName) {
      updateBody.label = state.newCohortName;
    }

    // Check if personal/company status changed
    if (state.selectedType !== originalState.selectedType) {
      updateBody.scope = state.selectedType;
    }

    // Always process filters when there are codes in any group
    if (state.billingCodes.groups.length > 0) {
      const currentGroup = state.billingCodes.groups[0];

      // Group codes by their properly mapped context_type
      const codesByType: Record<string, string[]> = {};

      currentGroup.codes.forEach((code) => {
        // Use the proper mapping function here
        const codeType = mapContextToCodeType(code.context_type);

        if (!codesByType[codeType]) {
          codesByType[codeType] = [];
        }
        codesByType[codeType].push(code.id);
      });

      // Transform into the filters array structure
      const newFilters = Object.entries(codesByType).map(([type, values]) => {
        // Look for an existing filter of this type to preserve its ID
        const existingFilter = currentCodeSet.filters?.find(
          (filter) => filter.type === type
        );

        const filterObj: any = {
          type,
          is_exclude: currentGroup.inverse,
          is_all: false,
          values,
        };

        // If this is an existing filter type, include its ID and filter_set
        if (existingFilter) {
          filterObj.id = existingFilter.id;
          filterObj.filter_set = parseInt(state.editingVolumeId || '0', 10);
        } else if (state.editingVolumeId) {
          // For new filter types, include filter_set reference
          filterObj.filter_set = parseInt(state.editingVolumeId, 10);
        }

        return filterObj;
      });

      // Always include the complete filters array in the update
      updateBody.filters = newFilters;
    }

    return updateBody;
  };

  // Helper to check if codes have changed
  const hasCodesChanged = (current, original) => {
    if (!current || !original) return true;
    if (current.length !== original.length) return true;

    // Create sets of code IDs for comparison
    const currentIds = new Set<string>(current.map((c) => c.id));
    const originalIds = new Set<string>(original.map((c) => c.id));

    // Check if any code is in one set but not the other
    const hasCurrentItemNotInOriginal = Array.from(currentIds).some(
      (id) => !originalIds.has(id)
    );
    const hasOriginalItemNotInCurrent = Array.from(originalIds).some(
      (id) => !currentIds.has(id)
    );

    return hasCurrentItemNotInOriginal || hasOriginalItemNotInCurrent;
  };

  const handleUpdate = async () => {
    if (!state.editingVolumeId && !currentCodeSet.id) {
      return;
    }

    try {
      const updateBody = buildUpdateRequestBody();

      // Ensure we have a proper filter ID (string)
      const filterId = currentCodeSet.id.toString();

      // Call the RTK Query mutation to update the filter set
      // Cache invalidation happens automatically via invalidatesTags
      await updateFilterSet({
        filterId,
        data: updateBody,
      }).unwrap();

      setNotification({
        title: 'Success',
        message: 'Code set updated successfully',
        type: 'success',
      });

      // Refresh folder data to ensure items in folders show updated information
      mutateAccountFolders();

      // Create the updated volume object that will be used to update any locations
      // where this code set is selected
      const updatedVolumeData: {
        id: number;
        label: string;
        type: 'CODE';
        scope: 'personal' | 'company';
        owner: any;
        filters: any[];
        created_at: string;
        status?: string;
        volumeType: 'filterSet';
      } = {
        id: currentCodeSet.id,
        label: state.newCohortName,
        type: currentCodeSet.type as 'CODE',
        scope:
          state.selectedType === SavedType.PERSONAL ? 'personal' : 'company',
        owner: currentCodeSet.owner,
        filters: updateBody.filters || currentCodeSet.filters,
        created_at: currentCodeSet.created_at,
        status: currentCodeSet.status,
        volumeType: 'filterSet',
      };

      // Update selected volume if the updated code set is currently selected
      if (
        appliedSavedVolume &&
        appliedSavedVolume.id === currentCodeSet.id &&
        appliedSavedVolume.volumeType === 'filterSet'
      ) {
        // Update the entire appliedSavedVolume with the updated code set data
        setSavedVolume(updatedVolumeData);
      }

      handleClose();
    } catch (error: any) {
      console.error('Error updating code set:', error);

      setNotification({
        title: 'Error',
        message:
          error?.data?.error_message ||
          'Failed to update code set. Please try again.',
        type: 'error',
      });
    }
  };

  // Disable buttons if form is empty or loading
  const isFormInvalid =
    !state.newCohortName.trim() ||
    state.billingCodes.groups.length === 0 ||
    state.billingCodes.groups.some((group) => group.codes.length === 0) ||
    isLoading ||
    isFetching;

  // Add a function to check if changes have been made
  const hasChanges = useCallback(() => {
    if (!originalState) return false;

    // Compare name
    if (state.newCohortName !== originalState.newCohortName) return true;

    // Compare type (personal/company)
    if (state.selectedType !== originalState.selectedType) return true;

    // Check each group's properties and codes
    for (let i = 0; i < state.billingCodes.groups.length; i++) {
      const currentGroup = state.billingCodes.groups[i];

      // If no original group exists at this index, something changed
      if (!originalState.billingCodes.groups[i]) return true;

      const originalGroup = originalState.billingCodes.groups[i];

      // Check operator and inverse
      if (currentGroup.operator !== originalGroup.operator) return true;
      if (currentGroup.inverse !== originalGroup.inverse) return true;

      // Check codes length
      if (currentGroup.codes.length !== originalGroup.codes.length) return true;

      // Check if codes changed
      return hasCodesChanged(currentGroup.codes, originalGroup.codes);
    }

    return false;
  }, [originalState, state.newCohortName, state.selectedType, state.billingCodes.groups]);

  // Update the button disable logic
  const isUpdateDisabled =
    isFormInvalid || // Original validation
    !hasChanges(); // New check for changes

  const handleCancel = () => {
    handleClose();
  };

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const handleBeforeClose = useCallback(() => {
    if (hasChanges()) {
      setShowDiscardDialog(true);
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardDialog(false);
    handleClose();
  }, [handleClose]);

  return (
    <>
      <Drawer
        isOpen={isDrawerOpen('editCodeSetsDrawer')}
        onClose={handleClose}
        hideBackdrop
        size="base"
        data-testid="edit-filter-sets-drawer"
        anchor="right"
        variant="temporary"
        onBeforeClose={handleBeforeClose}
      >
        <Box className="flex flex-col h-full relative">
          {/* Header */}
          <Drawer.Header className="bg-sky-100 text-primary border-b-0">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <p
                  className="font-bold text-lg"
                  data-testid="edit-codesets-drawer-title"
                >
                  Editing Code Set
                </p>
              </div>
              <div className="flex items-center gap-2">
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={handleCancel}
                  icon={faXmark}
                  className="text-primary hover:bg-sky-200"
                  data-testid="edit-codesets-drawer-close-button"
                />
              </div>
            </div>
          </Drawer.Header>
          {/* Content Area */}
          <Drawer.Content>
            {isFetching ? (
              <Box className="flex justify-center items-center p-8">
                <CircularProgress />
              </Box>
            ) : errorCurrentCodeSet ? (
              <Box className="flex justify-center items-center p-8">
                <Typography className="text-red-500">
                  Error loading code set data. Please try again.
                </Typography>
              </Box>
            ) : (
              <>
                <BillingCodes />
                <SearchPreview />
                <SaveVolumeType />
              </>
            )}
          </Drawer.Content>

          {/* Footer with Save and Update buttons */}
          <Drawer.Footer>
            <Box className="flex w-full gap-4">
              <Button
                variant="solid"
                className="flex-1 !rounded-full bg-neutral-500 hover:bg-neutral-600 text-white font-bold"
                onClick={handleSaveAsNew}
                disabled={isFormInvalid || isLoading}
              >
                Save As New...
              </Button>
              <Button
                variant="solid"
                className="flex-1 !rounded-full bg-sky-600 hover:bg-sky-800 text-white font-bold"
                onClick={handleUpdate}
                disabled={isUpdateDisabled}
                loading={isLoading}
              >
                Update
              </Button>
            </Box>
          </Drawer.Footer>
        </Box>
      </Drawer>

      {/* Render SaveNewVolumeDialog outside of the Drawer */}
      <SaveNewVolumeDialog
        isOpen={isSaveAsNewDialogOpen}
        onClose={() => setIsSaveAsNewDialogOpen(false)}
        volumeType={VolumeType.TOTAL_CLAIMS}
        currentName={state.newCohortName}
        currentType={state.selectedType}
        billingCodes={state.billingCodes}
        onSaveSuccess={() => {
          // RTK Query cache is auto-invalidated by SaveNewVolumeDialog
          // The useGetFilterSetByIdQuery will automatically refetch when cache is invalidated
        }}
        originalState={originalState}
      />
      <UnsavedChangesDialog
        isOpen={showDiscardDialog}
        onDiscard={handleDiscardConfirm}
        onCancel={() => setShowDiscardDialog(false)}
      />
    </>
  );
};

export default EditCodeSetsDrawer;
